import React, { useState, useEffect, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Grid,
    TablePagination,
    Tooltip,
    TableSortLabel,
    Card,
    CardContent,
    CardActions,
    Avatar,
    Switch,
    useTheme,
    useMediaQuery,
    styled,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Print as PrintIcon,
    GetApp as GetAppIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    FilterList as FilterIcon,
    ChevronLeft,
    ChevronRight,
} from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, query, orderBy, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { format, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import DatePicker from 'react-datepicker';

import { PedidoPreprogramado, Route } from '../models';

interface PreprogrammedOrder extends Omit<PedidoPreprogramado, 'id'> {
    id: string;
    originRouteName?: string;
    destinyRouteName?: string;
    clientName?: string;
    clientLocation: {
        provincia: string;
        canton: string;
        distrito: string;
    };
}

const PreprogrammedOrders: React.FC = () => {
    const [orders, setOrders] = useState<PreprogrammedOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [originRouteFilter, setOriginRouteFilter] = useState('');
    const [destinyRouteFilter, setDestinyRouteFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [dateRangeFilter, setDateRangeFilter] = useState<[Date | null, Date | null]>([
        new Date(),
        addMonths(new Date(), 1)
    ]);

    const uniqueOriginRoutes = useMemo(() => {
        const routes = new Set<string>();
        orders.forEach(order => {
            if (order.originRouteName) routes.add(order.originRouteName);
        });
        return Array.from(routes).sort();
    }, [orders]);

    const uniqueDestinyRoutes = useMemo(() => {
        const routes = new Set<string>();
        orders.forEach(order => {
            if (order.destinyRouteName) routes.add(order.destinyRouteName);
        });
        return Array.from(routes).sort();
    }, [orders]);

    const uniqueLocations = useMemo(() => {
        const locations = new Set<string>();
        orders.forEach(order => {
            if (order.clientLocation.provincia) locations.add(order.clientLocation.provincia);
        });
        return Array.from(locations).sort();
    }, [orders]);

    const locationColors: Record<string, string> = {
        'Alajuela': '#B3E5FC',
        'Cartago': '#D1C4E9',
        'Heredia': '#C8E6C9',
        'Guanacaste': '#FFF9C4',
        'Puntarenas': '#B2EBF2',
        'Limón': '#FFF59D',
        'San José': '#C5CAE9',
    };

    const getLocationColor = (provincia: string): string => {
        return locationColors[provincia] || 'transparent';
    };

    const filteredOrders = useMemo(() => {
        let result = orders;

        if (dateRangeFilter[0] && dateRangeFilter[1]) {
            const startDate = new Date(dateRangeFilter[0]);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(dateRangeFilter[1]);
            endDate.setHours(23, 59, 59, 999);

            result = result.filter(order => {
                const destinyDate = order.destinyReprogrammedDate;
                if (!destinyDate) return false;
                return destinyDate >= startDate && destinyDate <= endDate;
            });
        }

        if (originRouteFilter) {
            result = result.filter(order => order.originRouteName === originRouteFilter);
        }

        if (destinyRouteFilter) {
            result = result.filter(order => order.destinyRouteName === destinyRouteFilter);
        }

        if (locationFilter) {
            result = result.filter(order => order.clientLocation.provincia === locationFilter);
        }

        if (searchTerm) {
            const normalizedSearch = searchTerm.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            result = result.filter(order => {
                const clientName = (order.clientName || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                const originRoute = (order.originRouteName || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                const destinyRoute = (order.destinyRouteName || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                return clientName.includes(normalizedSearch) || originRoute.includes(normalizedSearch) || destinyRoute.includes(normalizedSearch);
            });
        }

        return result;
    }, [orders, dateRangeFilter, originRouteFilter, destinyRouteFilter, locationFilter, searchTerm]);

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadCSV = () => {
        // Create CSV header
        const headers = ['Cliente', 'Fecha Origen', 'Ruta Origen', 'Fecha Destino', 'Ruta Destino'];
        
        // Create CSV rows
        const csvRows = [
            headers.join(','),
            ...filteredOrders.map(order => [
                `"${order.clientName || ''}"`,
                `"${order.originReprogrammedDate ? format(order.originReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}"`,
                `"${order.originRouteName || ''}"`,
                `"${order.destinyReprogrammedDate ? format(order.destinyReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}"`,
                `"${order.destinyRouteName || ''}"`
            ].join(','))
        ];
        
        // Create CSV content
        const csvContent = csvRows.join('\n');
        
        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `pedidos_preprogramados_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to get client name and location by ID
    const getClientData = async (clientId: string): Promise<{ name: string; location: { provincia: string; canton: string; distrito: string } }> => {
        try {
            const clientDoc = await getDoc(doc(db, 'clients', clientId));
            if (clientDoc.exists()) {
                const data = clientDoc.data();
                return {
                    name: data.name || 'Cliente no encontrado',
                    location: {
                        provincia: data.provincia || '',
                        canton: data.canton || '',
                        distrito: data.distrito || ''
                    }
                };
            }
            return { name: 'Cliente no encontrado', location: { provincia: '', canton: '', distrito: '' } };
        } catch (error) {
            console.error('Error fetching client:', error);
            return { name: 'Error al cargar', location: { provincia: '', canton: '', distrito: '' } };
        }
    };

    // Function to get route name by ID
    const getRouteName = async (routeId: string): Promise<string> => {
        try {
            const routeDoc = await getDoc(doc(db, 'routeGroups', routeId));
            if (routeDoc.exists()) {
                return routeDoc.data().name || 'Ruta sin nombre';
            }
            return 'Ruta no encontrada';
        } catch (error) {
            console.error('Error fetching route:', error);
            return 'Error al cargar';
        }
    };

    useEffect(() => {
        fetchPreprogrammedOrders();
    }, []);

    const fetchPreprogrammedOrders = async () => {
        try {
            setLoading(true);
            const ordersRef = collection(db, 'pedidoPreprogramado');
            const q = query(ordersRef, orderBy('destinyReprogrammedDate', 'asc'));
            const querySnapshot = await getDocs(q);

            const ordersData = await Promise.all(querySnapshot.docs.map(async (doc) => {
                const data = doc.data();
                const originRouteName = await getRouteName(data.originReprogrammedRouteGroupId);
                const destinyRouteName = await getRouteName(data.destinyReprogrammedRouteGroupId);
                let clientData = { name: 'No asignado', location: { provincia: '', canton: '', distrito: '' } };

                if (data.routeRreprogrammed?.clientId) {
                    clientData = await getClientData(data.routeRreprogrammed.clientId);
                }

                return {
                    id: doc.id,
                    originReprogrammedRouteGroupId: data.originReprogrammedRouteGroupId,
                    destinyReprogrammedRouteGroupId: data.destinyReprogrammedRouteGroupId,
                    originReprogrammedDate: data.originReprogrammedDate?.toDate(),
                    destinyReprogrammedDate: data.destinyReprogrammedDate?.toDate(),
                    routeRreprogrammed: data.routeRreprogrammed,
                    originRouteName,
                    destinyRouteName,
                    clientName: clientData.name,
                    clientLocation: clientData.location
                } as PreprogrammedOrder;
            }));

            setOrders(ordersData);
        } catch (error) {
            console.error('Error fetching preprogrammed orders:', error);
            toast.error('Error al cargar los pedidos preprogramados');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (orderId: string | string[]) => {
        setOrderToDelete(Array.isArray(orderId) ? orderId[0] : orderId);
        setDeleteDialogOpen(true);
    };

    const handleBulkDelete = () => {
        if (selectedOrders.length > 0) {
            setOrderToDelete(selectedOrders[0]); // Set the first ID for the dialog message
            setDeleteDialogOpen(true);
        }
    };

    // Helper function to normalize strings by removing accents
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleDeleteConfirmOne = async () => {
        if (!orderToDelete) return;

        try {
            await deleteDoc(doc(db, 'pedidoPreprogramado', orderToDelete));
            const updatedOrders = orders.filter(order => order.id !== orderToDelete);
            setOrders(updatedOrders);
            toast.success('Pedido preprogramado eliminado exitosamente');
        } catch (error) {
            console.error('Error deleting preprogrammed order:', error);
            toast.error('Error al eliminar el pedido preprogramado');
        } finally {
            setDeleteDialogOpen(false);
            setOrderToDelete(null);
        }
    };

    const handleDeleteConfirm = async (ids: string | string[]) => {
        const idsToDelete = Array.isArray(ids) ? ids : [ids];
        
        try {
            const deletePromises = idsToDelete.map(id => 
                deleteDoc(doc(db, 'pedidoPreprogramado', id))
            );
            
            await Promise.all(deletePromises);
            
            const updatedOrders = orders.filter(order => !idsToDelete.includes(order.id));
            setOrders(updatedOrders);
            setSelectedOrders(prev => prev.filter(id => !idsToDelete.includes(id)));
            
            toast.success(idsToDelete.length > 1 ? 
                'Pedidos preprogramados eliminados exitosamente' : 
                'Pedido preprogramado eliminado exitosamente'
            );
        } catch (error) {
            console.error('Error deleting preprogrammed order(s):', error);
            toast.error(`Error al eliminar ${idsToDelete.length > 1 ? 'los pedidos' : 'el pedido'} preprogramado`);
        } finally {
            setDeleteDialogOpen(false);
            setOrderToDelete(null);
        }
    };

    const handleSelectOrder = (orderId: string) => {
        setSelectedOrders(prev => 
            prev.includes(orderId)
                ? prev.filter(id => id !== orderId)
                : [...prev, orderId]
        );
    };

    const handleSelectAllOrders = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(order => order.id);
            setSelectedOrders(newSelected);
        } else {
            setSelectedOrders([]);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'success.main';
            case 'paused':
                return 'warning.main';
            case 'completed':
                return 'text.secondary';
            default:
                return 'text.primary';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'active':
                return 'Activo';
            case 'paused':
                return 'Pausado';
            case 'completed':
                return 'Completado';
            default:
                return status;
        }
    };

    // Remove getFrequencyText as it's no longer needed

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                color: 'rgb(0, 180, 216)'
            }}>
                <Typography variant="h5" component="h1">
                    Pedidos Preprogramados
                    {selectedOrders.length > 0 && (
                        <Typography component="span" sx={{ ml: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
                            {selectedOrders.length} seleccionado(s)
                        </Typography>
                    )}
                </Typography>
                <Box>
                    {selectedOrders.length > 0 ? (  
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleBulkDelete}
                            sx={{ mr: 1 }}
                        >
                            Eliminar seleccionados
                        </Button>
                    ) : null}
                </Box>
            </Box>
            <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgb(0, 180, 216)',
                margin: '20px 0'
            }} />
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'rgb(0, 180, 216)', fontWeight: 'bold' }}>
                    Selecciona el rango de fechas
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <label style={{ color: 'rgb(0, 180, 216)', fontWeight: 'bold', fontSize: '0.875rem' }}>Fecha de inicio</label>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={() => setDateRangeFilter([addMonths(dateRangeFilter[0] || new Date(), -1), dateRangeFilter[1]])} size="small">
                                <ChevronLeft />
                            </IconButton>
                            <DatePicker
                                selected={dateRangeFilter[0]}
                                onChange={(date) => setDateRangeFilter([date, dateRangeFilter[1]])}
                                selectsStart
                                startDate={dateRangeFilter[0] || undefined}
                                endDate={dateRangeFilter[1] || undefined}
                                dateFormat="dd/MM/yyyy"
                                customInput={<TextField variant="outlined" size="small" sx={{ width: 140 }} />}
                                locale={es}
                                popperPlacement="bottom-start"
                            />
                            <IconButton onClick={() => setDateRangeFilter([addMonths(dateRangeFilter[0] || new Date(), 1), dateRangeFilter[1]])} size="small">
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <label style={{ color: 'rgb(0, 180, 216)', fontWeight: 'bold', fontSize: '0.875rem' }}>Fecha de fin</label>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={() => setDateRangeFilter([dateRangeFilter[0], addMonths(dateRangeFilter[1] || new Date(), -1)])} size="small">
                                <ChevronLeft />
                            </IconButton>
                            <DatePicker
                                selected={dateRangeFilter[1]}
                                onChange={(date) => setDateRangeFilter([dateRangeFilter[0], date])}
                                selectsEnd
                                startDate={dateRangeFilter[0] || undefined}
                                endDate={dateRangeFilter[1] || undefined}
                                minDate={dateRangeFilter[0] || undefined}
                                dateFormat="dd/MM/yyyy"
                                customInput={<TextField variant="outlined" size="small" sx={{ width: 140 }} />}
                                locale={es}
                                popperPlacement="bottom-start"
                            />
                            <IconButton onClick={() => setDateRangeFilter([dateRangeFilter[0], addMonths(dateRangeFilter[1] || new Date(), 1)])} size="small">
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Ruta Origen</InputLabel>
                        <Select
                            value={originRouteFilter}
                            onChange={(e) => setOriginRouteFilter(e.target.value)}
                            label="Ruta Origen"
                            sx={{ '& .MuiSelect-select': { py: 0.75 } }}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {uniqueOriginRoutes.map((route) => (
                                <MenuItem key={route} value={route}>{route}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Ruta Destino</InputLabel>
                        <Select
                            value={destinyRouteFilter}
                            onChange={(e) => setDestinyRouteFilter(e.target.value)}
                            label="Ruta Destino"
                            sx={{ '& .MuiSelect-select': { py: 0.75 } }}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {uniqueDestinyRoutes.map((route) => (
                                <MenuItem key={route} value={route}>{route}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontSize: '0.875rem' }}>Ubicación</InputLabel>
                        <Select
                            value={locationFilter}
                            onChange={(e) => setLocationFilter(e.target.value)}
                            label="Ubicación"
                            sx={{ '& .MuiSelect-select': { py: 0.75 } }}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {uniqueLocations.map((location) => (
                                <MenuItem key={location} value={location}>{location}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                        }}
                        sx={{ width: 200 }}
                    />
                    <Tooltip title="Imprimir">
                        <IconButton onClick={handlePrint}>
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar CSV">
                        <IconButton onClick={handleDownloadCSV}>
                            <GetAppIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, minHeight: 'calc(60vh)' }}>
                <Table sx={{ minWidth: 650 }} aria-label="tabla de pedidos preprogramados">
                    <TableHead sx={{ backgroundColor: 'rgb(0, 180, 216)' }}>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAllOrders}
                                    checked={selectedOrders.length > 0 && 
                                            selectedOrders.length === filteredOrders
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                                    style={{ cursor: 'pointer' }}
                                />
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    backgroundColor: locationFilter ? 'rgba(255,255,255,0.3)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                                }}
                                onClick={() => {
                                    if (!locationFilter) {
                                        if (uniqueLocations.length > 0) setLocationFilter(uniqueLocations[0]);
                                    } else {
                                        const idx = uniqueLocations.indexOf(locationFilter);
                                        if (idx < uniqueLocations.length - 1) setLocationFilter(uniqueLocations[idx + 1]);
                                        else setLocationFilter('');
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Ubicación
                                    {locationFilter && <FilterIcon sx={{ fontSize: 14 }} />}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Origen</TableCell>
                            <TableCell 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    backgroundColor: originRouteFilter ? 'rgba(255,255,255,0.3)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                                }}
                                onClick={() => {
                                    if (!originRouteFilter) {
                                        if (uniqueOriginRoutes.length > 0) setOriginRouteFilter(uniqueOriginRoutes[0]);
                                    } else {
                                        const idx = uniqueOriginRoutes.indexOf(originRouteFilter);
                                        if (idx < uniqueOriginRoutes.length - 1) setOriginRouteFilter(uniqueOriginRoutes[idx + 1]);
                                        else setOriginRouteFilter('');
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Ruta Origen
                                    {originRouteFilter && <FilterIcon sx={{ fontSize: 14 }} />}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha Destino</TableCell>
                            <TableCell 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    backgroundColor: destinyRouteFilter ? 'rgba(255,255,255,0.3)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                                }}
                                onClick={() => {
                                    if (!destinyRouteFilter) {
                                        if (uniqueDestinyRoutes.length > 0) setDestinyRouteFilter(uniqueDestinyRoutes[0]);
                                    } else {
                                        const idx = uniqueDestinyRoutes.indexOf(destinyRouteFilter);
                                        if (idx < uniqueDestinyRoutes.length - 1) setDestinyRouteFilter(uniqueDestinyRoutes[idx + 1]);
                                        else setDestinyRouteFilter('');
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    Ruta Destino
                                    {destinyRouteFilter && <FilterIcon sx={{ fontSize: 14 }} />}
                                </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ minHeight: '400px' }}>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ height: '470px', border: 'none' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        height: '100%',
                                        width: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <CircularProgress size={60} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : filteredOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No se encontraron resultados para la búsqueda
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredOrders
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((order) => {
                                    const locationColor = getLocationColor(order.clientLocation.provincia);
                                    return (
                                    <TableRow 
                                        key={order.id}
                                        hover
                                        selected={selectedOrders.includes(order.id)}
                                        onClick={() => handleSelectOrder(order.id)}
                                        style={{ 
                                            cursor: 'pointer',
                                            backgroundColor: order.clientLocation.provincia ? locationColor : 'transparent'
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            {selectedOrders.includes(order.id) ? (
                                                <CheckBoxIcon color="primary" />
                                            ) : (
                                                <CheckBoxOutlineBlankIcon />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: order.clientLocation.provincia ? 600 : 400 }}>
                                            {order.clientName || 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontSize: '0.75rem',
                                                    cursor: order.clientLocation.provincia ? 'pointer' : 'default',
                                                    '&:hover': order.clientLocation.provincia ? { textDecoration: 'underline', color: 'rgb(0, 180, 216)' } : {}
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (order.clientLocation.provincia) setLocationFilter(order.clientLocation.provincia);
                                                }}
                                            >
                                                {order.clientLocation.provincia && (
                                                    <><strong>{order.clientLocation.provincia}</strong><br /></>
                                                )}
                                                {order.clientLocation.canton && (
                                                    <>{order.clientLocation.canton}<br /></>
                                                )}
                                                {order.clientLocation.distrito && (
                                                    <>{order.clientLocation.distrito}</>
                                                )}
                                                {!order.clientLocation.provincia && '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {order.originReprogrammedDate ? format(order.originReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline', color: 'rgb(0, 180, 216)' }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (order.originRouteName) setOriginRouteFilter(order.originRouteName);
                                            }}
                                        >
                                            {order.originRouteName || 'Cargando...'}
                                        </TableCell>
                                        <TableCell>
                                            {order.destinyReprogrammedDate ? format(order.destinyReprogrammedDate, 'PPP', { locale: es }) : 'N/A'}
                                        </TableCell>
                                        <TableCell 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline', color: 'rgb(0, 180, 216)' }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (order.destinyRouteName) setDestinyRouteFilter(order.destinyRouteName);
                                            }}
                                        >
                                            {order.destinyRouteName || 'Cargando...'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box
                                                className="action-icons"
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    justifyContent: 'flex-end',
                                                    opacity: { xs: 1, md: 0.7 },
                                                    '&:hover': {
                                                        opacity: 1
                                                    }
                                                }}
                                            >
                                                <Tooltip title="Eliminar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(order.id);
                                                        }}
                                                        sx={{
                                                            color: 'error.main',
                                                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[7, 11, 25]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        margin: 0,
                    },
                    '.MuiTablePagination-toolbar': {
                        paddingLeft: 2,
                        paddingRight: 2,
                    },
                    mt: 1,
                    borderTop: '1px solid rgba(224, 224, 224, 0.5)'
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                fullScreen={window.innerWidth < 600}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                    },
                }}>
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, paddingTop: '29px !important' }}>
                    <Box sx={{
                        backgroundColor: 'red',
                        borderRadius: '50%',
                        padding: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 2
                    }}>
                        <CloseIcon sx={{ color: 'white', fontSize: 40 }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
                    <Button 
                        onClick={() => {
                            if (selectedOrders.length > 1) {
                                handleDeleteConfirm(selectedOrders);
                            } else {
                                handleDeleteConfirmOne();
                            }
                        }}
                        variant="contained"
                        sx={{
                            backgroundColor: 'red',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgb(200, 0, 0)',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'rgba(0, 0, 0, 0.26)',
                            },
                            fontWeight: 'bold',
                            px: 3,
                            py: 1,
                        }}
                        autoFocus
                    >
                        {selectedOrders.length > 1 ? `Eliminar ${selectedOrders.length} pedidos` : 'Eliminar'}
                    </Button>
                    <Button 
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setOrderToDelete(null);
                        }}
                        variant="outlined"
                        sx={{
                            color: 'rgb(0, 180, 216)',
                            borderColor: 'rgb(0, 180, 216)',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                borderColor: 'rgb(0, 180, 216)',
                            },
                            fontWeight: 'bold',
                            px: 3,
                            py: 1,
                        }}
                    >
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PreprogrammedOrders;
